import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import {
  QUARTERLY_OKR,
  QUARTERLY_OKR_BRIEF,
  QUARTERLY_OKR_REVIEW,
} from "../../../../data/RouteUrls";
import {
  REVIEWABLE_STATES,
  REVIEW_SENT_STATES,
} from "../_config/quarterlyOkrConfig";
import { ctaForQuarter, routeForQuarter } from "../_config/quarterlyOkrRoutes";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";
import useCoachBrief from "./useCoachBrief";
import {
  BRIEF_VIEW,
  asArray,
  briefViewOf,
  buildObjectiveCards,
  formatGeneratedAt,
} from "./briefModel";
import BriefStates from "./BriefStates/BriefStates";
import BriefOverview from "./BriefOverview/BriefOverview";
import BriefObjectiveCard from "./BriefObjectiveCard/BriefObjectiveCard";
import BriefCoverageTable from "./BriefCoverageTable/BriefCoverageTable";
import BriefTeamTable from "./BriefTeamTable/BriefTeamTable";
import BriefRegenerateDialog from "./BriefRegenerateDialog/BriefRegenerateDialog";
import "../../StrategyCanvas/StrategyCanvas.scss";
import "../../StrategyCanvas/StrategyCanvasBrief.scss";
import "./_quarterly-okr-brief.scss";

// ═══════════════════════════════════════════════════════════════════════════
// `/coach-panel/quarterly-okrs/:quarterCompanyId/brief` — the AI coach brief
// (handoff 34). The Strategy Canvas brief's twin (`#strategy-canvas-brief`,
// `scb-*`): company name and the quarter's next step in the header, tabs
// under it, the content on the cream panel.
//
//   • The dashboard opens here while the quarter is with the coach; the
//     header button goes on to the review (or wherever the quarter is now).
//   • Never blocks: every not-ready state still offers that button.
//   • Reading the brief does NOT call `review/enter` — only opening the
//     Review does (§2). The review GET is read for card order, titles and
//     members, and its failure is ignored.
//   • Coach only. Nothing here exists in the company app.
// ═══════════════════════════════════════════════════════════════════════════

const TAB = {
  SUMMARY: "Summary",
  OBJECTIVES: "Company Objectives",
  COVERAGE: "Annual OKR Coverage",
  TEAM: "Team",
};

const selectClients = (state) => state.clients;
const selectReviews = (state) => state.reviewByQc;

const QuarterlyOkrBrief = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const HeaderMetaData = useContext(HeaderContext);
  const { quarterCompanyId: qcId } = useParams();

  const clients = useQuarterlyOkrStore(selectClients);
  const review = useQuarterlyOkrStore(selectReviews)[qcId];
  const { response, error, regenerating, canRegenerate, regenerate, retry } =
    useCoachBrief(qcId);

  const [tab, setTab] = useState(TAB.SUMMARY);
  const [confirming, setConfirming] = useState(false);

  // The dashboard row: company, quarter label, run state, not-completed.
  const { client, row } = useMemo(() => {
    for (const c of clients || []) {
      const q = (c.quarters || []).find(
        (item) => String(item.quarter_company_id) === String(qcId),
      );
      if (q) return { client: c, row: q };
    }
    return { client: null, row: null };
  }, [clients, qcId]);

  const companyName = review?.company?.name || client?.company_name || "";
  const quarterLabel = row?.label || "";

  useEffect(() => {
    document.title = "Art of Scale | Quarterly OKR Brief";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Quarterly OKRs" });
    const store = quarterlyOkrStore.getState();
    if (!store.clients) store.fetchDashboard().catch(() => null);
    store.fetchReview(qcId, { force: true }).catch(() => null);
    setTab(TAB.SUMMARY);
    // eslint-disable-next-line
  }, [qcId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(QUARTERLY_OKR_BRIEF, [
        breadCrumbsArray[QUARTERLY_OKR][0],
        { title: companyName || "Brief", url: "" },
      ]),
    );
  }, [dispatch, companyName]);

  // The quarter's next step — the review's own route, never back here.
  const runState = review?.run_state || row?.run_state || null;
  const nextRoute = runState
    ? routeForQuarter({
        quarter_company_id: qcId,
        run_state: runState,
        not_completed: row?.not_completed,
      })
    : null;
  const nextLabel = runState
    ? ctaForQuarter({ run_state: runState, not_completed: row?.not_completed })
    : "Open review";
  const next = nextRoute
    ? { label: nextLabel, onClick: () => history.push(nextRoute) }
    : null;

  // "Go to objective" / a member's name → the Review, opened on that row.
  const reviewRoute = QUARTERLY_OKR_REVIEW.replace(":quarterCompanyId", qcId);
  const reviewOpen =
    REVIEWABLE_STATES.includes(runState) ||
    REVIEW_SENT_STATES.includes(runState);
  const goToObjective = reviewOpen
    ? (id) => history.push(`${reviewRoute}?objective=${encodeURIComponent(id)}`)
    : null;
  const openMember = reviewOpen
    ? (member) =>
        history.push(
          `${reviewRoute}?member=${encodeURIComponent(member.user_id)}`,
        )
    : null;

  const view = briefViewOf(response);
  const brief = view === BRIEF_VIEW.READY ? response.brief : null;
  const cards = useMemo(
    () => (brief ? buildObjectiveCards(brief, review) : []),
    [brief, review],
  );
  const generatedAt = formatGeneratedAt(response?.generated_at);

  const confirmRegenerate = () =>
    regenerate().finally(() => setConfirming(false));

  const renderTab = () => {
    if (tab === TAB.OBJECTIVES) {
      return cards.length ? (
        <div className="qokr-brief-objs">
          {cards.map((card) => (
            <BriefObjectiveCard
              key={card.id}
              card={card}
              onGoTo={goToObjective}
            />
          ))}
        </div>
      ) : (
        <div className="scb-empty-state">
          The brief has no company objectives.
        </div>
      );
    }
    if (tab === TAB.COVERAGE) {
      return <BriefCoverageTable rows={brief.annual_coverage} />;
    }
    if (tab === TAB.TEAM) {
      return (
        <BriefTeamTable
          rows={brief.team}
          review={review}
          onOpenMember={openMember}
        />
      );
    }
    return <BriefOverview brief={brief} />;
  };

  const tabCount = {
    [TAB.OBJECTIVES]: cards.length,
    [TAB.COVERAGE]: asArray(brief?.annual_coverage).length,
    [TAB.TEAM]: asArray(brief?.team).length,
  };

  return (
    <div>
      <ProjectHeader
        handleNavigation={() => history.push(QUARTERLY_OKR)}
        showBack
      />
      <div id="strategy-canvas-brief" className="qokr-brief">
        <div className="scb-header">
          <div className="scb-title">
            {companyName}
            {quarterLabel ? (
              <span className="qokr-brief__quarter">
                <i className="fa-regular fa-calendar" aria-hidden="true" />
                {quarterLabel}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="scb-cta-btn"
            disabled={!next}
            onClick={next?.onClick}
          >
            {next ? next.label : nextLabel}
            <img
              src={"/assets/images/right-arrow-white.svg"}
              alt=""
              className="scb-cta-icon"
            />
          </button>
        </div>

        <div className="scb-tabs">
          {brief ? (
            Object.values(TAB).map((label) => (
              <button
                key={label}
                type="button"
                className={`scb-tab ${tab === label ? "active" : ""}`}
                onClick={() => setTab(label)}
              >
                {label}
                {tabCount[label] ? (
                  <span className="qokr-brief__tab-count">
                    {tabCount[label]}
                  </span>
                ) : null}
              </button>
            ))
          ) : (
            <span className="scb-tab active">Brief</span>
          )}
        </div>

        <div className="scb-content">
          <div className="scb-subheader scb-subheader-split">
            <span className="scb-subheader-text">
              <i className="fa-solid fa-lock" aria-hidden="true" /> Written by
              AI for you only. The CEO and the team don&apos;t see it.
            </span>
            {brief ? (
              <span className="qokr-brief__meta">
                {/* No Regenerate on a finished brief (QOKR UAT round 2): it only
                    made the summary vanish while a new one was written. The
                    FAILED state in BriefStates keeps it, to recover from. */}
                {generatedAt ? <span>Generated {generatedAt}</span> : null}
              </span>
            ) : null}
          </div>

          {brief ? (
            renderTab()
          ) : (
            <BriefStates
              view={view}
              error={response ? null : error}
              canRegenerate={canRegenerate}
              regenerating={regenerating}
              onRegenerate={() => setConfirming(true)}
              onRetry={retry}
              next={next}
            />
          )}
        </div>
      </div>

      {confirming ? (
        <BriefRegenerateDialog
          busy={regenerating}
          onConfirm={confirmRegenerate}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
};

export default QuarterlyOkrBrief;
